/**
 * Theme Presets
 * Predefined theme configurations that can be applied by name
 */

import type { EmbedThemeConfig, ThemePreset } from './types';

// ============================================================================
// Predefined Theme Presets
// ============================================================================

/**
 * Default theme - Clean, modern look with blue accent
 */
export const defaultPreset: ThemePreset = {
  name: 'default',
  config: {
    mode: 'light',
    primaryColor: '#3b82f6',
    borderRadius: 'lg',
  },
};

/**
 * Dark theme - Dark mode with blue accent
 */
export const darkPreset: ThemePreset = {
  name: 'dark',
  config: {
    mode: 'dark',
    primaryColor: '#3b82f6',
    borderRadius: 'lg',
  },
};

/**
 * Modern theme - Contemporary look with purple accent
 */
export const modernPreset: ThemePreset = {
  name: 'modern',
  config: {
    mode: 'light',
    primaryColor: '#8b5cf6',
    borderRadius: 'lg',
  },
};

/**
 * Minimal theme - Clean, subtle design with gray tones
 */
export const minimalPreset: ThemePreset = {
  name: 'minimal',
  config: {
    mode: 'light',
    primaryColor: '#6b7280',
    borderRadius: 'md',
  },
};

/**
 * Bold theme - Vibrant orange accent
 */
export const boldPreset: ThemePreset = {
  name: 'bold',
  config: {
    mode: 'light',
    primaryColor: '#f97316',
    borderRadius: 'lg',
  },
};

/**
 * Ocean theme - Teal/cyan accent
 */
export const oceanPreset: ThemePreset = {
  name: 'ocean',
  config: {
    mode: 'light',
    primaryColor: '#0891b2',
    borderRadius: 'lg',
  },
};

/**
 * Forest theme - Green accent
 */
export const forestPreset: ThemePreset = {
  name: 'forest',
  config: {
    mode: 'light',
    primaryColor: '#059669',
    borderRadius: 'lg',
  },
};

/**
 * Rose theme - Pink/rose accent
 */
export const rosePreset: ThemePreset = {
  name: 'rose',
  config: {
    mode: 'light',
    primaryColor: '#e11d48',
    borderRadius: 'lg',
  },
};

/**
 * Midnight theme - Dark mode with indigo accent
 */
export const midnightPreset: ThemePreset = {
  name: 'midnight',
  config: {
    mode: 'dark',
    primaryColor: '#6366f1',
    borderRadius: 'lg',
  },
};

/**
 * Ember theme - Dark mode with warm orange accent
 */
export const emberPreset: ThemePreset = {
  name: 'ember',
  config: {
    mode: 'dark',
    primaryColor: '#f97316',
    borderRadius: 'lg',
  },
};

/**
 * Rounded theme - Extra rounded-sm corners
 */
export const roundedPreset: ThemePreset = {
  name: 'rounded-sm',
  config: {
    mode: 'light',
    primaryColor: '#3b82f6',
    borderRadius: 'full',
  },
};

/**
 * Sharp theme - No rounded-sm corners
 */
export const sharpPreset: ThemePreset = {
  name: 'sharp',
  config: {
    mode: 'light',
    primaryColor: '#3b82f6',
    borderRadius: 'none',
  },
};

// ============================================================================
// Preset Registry
// ============================================================================

/**
 * All available theme presets indexed by name
 */
export const THEME_PRESETS: Record<string, ThemePreset> = {
  default: defaultPreset,
  light: defaultPreset, // Alias
  dark: darkPreset,
  modern: modernPreset,
  minimal: minimalPreset,
  bold: boldPreset,
  ocean: oceanPreset,
  forest: forestPreset,
  rose: rosePreset,
  midnight: midnightPreset,
  ember: emberPreset,
  rounded: roundedPreset,
  sharp: sharpPreset,
};

/**
 * List of available preset names
 */
export const PRESET_NAMES = Object.keys(THEME_PRESETS);

/**
 * Type for valid preset names
 */
export type ThemePresetName = keyof typeof THEME_PRESETS;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get a theme preset by name
 * @param name - The preset name
 * @returns The theme preset config or undefined if not found
 */
export function getPreset(name: string): EmbedThemeConfig | undefined {
  const preset = THEME_PRESETS[name.toLowerCase()];
  return preset?.config;
}

/**
 * Check if a string is a valid preset name
 * @param name - The name to check
 * @returns True if the name is a valid preset
 */
export function isPresetName(name: string): name is ThemePresetName {
  return name.toLowerCase() in THEME_PRESETS;
}

/**
 * Resolve theme input to a theme config
 * Accepts:
 * - String preset name (e.g., 'dark', 'modern')
 * - EmbedThemeConfig object
 * - undefined (returns default)
 *
 * @param theme - Theme input (string name or config object)
 * @returns Resolved EmbedThemeConfig
 */
export function resolveTheme(theme: string | EmbedThemeConfig | undefined): EmbedThemeConfig {
  // No theme provided - use default
  if (!theme) {
    return defaultPreset.config;
  }

  // String - look up preset
  if (typeof theme === 'string') {
    const preset = getPreset(theme);
    if (preset) {
      return preset;
    }
    // Unknown preset name - check if it's 'light' or 'dark' for backward compatibility
    if (theme === 'light' || theme === 'dark') {
      return { mode: theme };
    }
    // Fallback to default
    console.warn(`[BolderWidget] Unknown theme preset: "${theme}". Using default.`);
    return defaultPreset.config;
  }

  // Object - use as-is
  return theme;
}

/**
 * Merge theme configs, with later configs taking precedence
 * @param configs - Theme configs to merge
 * @returns Merged theme config
 */
export function mergeThemeConfigs(...configs: (EmbedThemeConfig | undefined)[]): EmbedThemeConfig {
  const result: EmbedThemeConfig = {};

  for (const config of configs) {
    if (!config) continue;

    if (config.mode !== undefined) result.mode = config.mode;
    if (config.primaryColor !== undefined) result.primaryColor = config.primaryColor;
    if (config.fontFamily !== undefined) result.fontFamily = config.fontFamily;
    if (config.borderRadius !== undefined) result.borderRadius = config.borderRadius;

    if (config.colors) {
      result.colors = result.colors || {};
      if (config.colors.background) {
        result.colors.background = { ...result.colors.background, ...config.colors.background };
      }
      if (config.colors.text) {
        result.colors.text = { ...result.colors.text, ...config.colors.text };
      }
      if (config.colors.border) {
        result.colors.border = { ...result.colors.border, ...config.colors.border };
      }
    }
  }

  return result;
}
